import { Inbox, Pencil, Plus, SearchX, Trash2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useNavigate } from 'react-router';
import { Banner } from './Banner';
import { Button } from './Button';
import { Card } from './Card';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { type Column, DataTable } from './DataTable';
import { DensityControl } from './DensityControl';
import { EmptyState } from './EmptyState';
import { Page } from './Page';
import { Pagination } from './Pagination';
import { SearchFilter } from './SearchFilter';
import { useCan } from '#client/hooks/useCan';
import { useResourceList } from '#client/hooks/useResourceList';
import type { Permission } from '#shared/types';
import { countOf } from '#client/lib/format';

interface ResourceListPageProps<T extends { id: number }> {
  title: string;
  subtitle?: string | undefined;
  /** API path and route base, e.g. `/panels`. They are the same by convention. */
  path: string;
  columns: Column<T>[];
  /** Singular noun for buttons and prompts, e.g. "panel". */
  noun: string;
  /** Plural noun for the pagination summary, e.g. "panels". */
  nounPlural: string;
  searchPlaceholder: string;
  /** Label shown in the delete confirmation for a given row. */
  rowLabel: (row: T) => string;
  /**
   * The capability required to add or edit on this screen.
   *
   * The list itself is already gated by the route, so this governs only the
   * write affordances. They are hidden rather than disabled, because a role
   * that cannot manage a resource has no use for a greyed-out "Add" button
   * sitting in its way — and offering one that lands on a 403 screen is worse
   * than offering nothing.
   *
   * Omit for screens where anyone who can see the list can also edit it.
   * `createCapability` and `editCapability` override it for the one button
   * each, so a role may add records without changing old ones, or the reverse.
   */
  manageCapability?: Permission | undefined;
  /** The permission behind "Add". Defaults to `manageCapability`. */
  createCapability?: Permission | undefined;
  /** The permission behind each row's Edit. Defaults to `manageCapability`. */
  editCapability?: Permission | undefined;
  /**
   * A per-row rule, ANDed with `deleteCapability`.
   *
   * The same escape hatch `ResourceDetailPage` has, for rules that are about
   * the record rather than the role — an admin may delete users, but not their
   * own account.
   */
  canDeleteRow?: ((row: T) => boolean) | undefined;
  /**
   * The capability required to delete, when it differs from `manageCapability`.
   *
   * Patients are the case that forced this apart: a receptionist registers and
   * edits them but may not delete one, and driving both buttons from a single
   * capability put a Delete in front of them that the server refuses.
   *
   * Defaults to `manageCapability`.
   */
  deleteCapability?: Permission | undefined;
  /** Extra actions rendered before edit/delete. */
  extraRowActions?: ((row: T, list: { refresh: () => void }) => ReactNode) | undefined;
  /** One line explaining what this resource is, shown when the list is empty. */
  emptyBody?: string | undefined;
}

/**
 * The list screen shared by every simple resource: toolbar, table, row actions,
 * delete confirmation and pagination, so the fetch/search/paginate logic
 * exists once rather than per route file.
 */
export function ResourceListPage<T extends { id: number }>({
  title,
  subtitle,
  path,
  columns,
  noun,
  nounPlural,
  searchPlaceholder,
  rowLabel,
  canDeleteRow,
  extraRowActions,
  emptyBody,
  ...gates
}: ResourceListPageProps<T>) {
  const navigate = useNavigate();
  const list = useResourceList<T>({ path });
  const { canCreate, canEdit, canDelete } = useWriteAccess(gates);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; label: string } | null>(null);

  const addAction = canCreate ? (
    <Button variant="primary" icon={<Plus />} onClick={() => void navigate(`${path}/new`)}>
      Add {noun}
    </Button>
  ) : undefined;

  return (
    <Page title={title} subtitle={subtitle} primaryAction={addAction} fullWidth>
      {list.error && <Banner tone="critical">{list.error}</Banner>}

      <Card flush>
        <ListToolbar
          search={list.search}
          onSearch={list.setSearch}
          placeholder={searchPlaceholder}
          countLabel={list.loading ? undefined : countOf(list.count, noun, nounPlural)}
        />

        <DataTable
          caption={title}
          columns={columns}
          rows={list.items}
          rowKey={(row) => row.id}
          loading={list.loading}
          skeletonRows={Math.min(list.limit, 10)}
          emptyState={
            <ListEmptyState
              search={list.search}
              nounPlural={nounPlural}
              emptyBody={emptyBody}
              addAction={addAction}
              onClearSearch={() => list.setSearch('')}
            />
          }
          onRowClick={(row) => void navigate(`${path}/${row.id}`)}
          rowActions={(row) => (
            <RowActions
              label={rowLabel(row)}
              extra={extraRowActions?.(row, { refresh: list.refresh })}
              onEdit={canEdit ? () => void navigate(`${path}/${row.id}/edit`) : undefined}
              onDelete={
                canDelete && (canDeleteRow?.(row) ?? true)
                  ? () => setDeleteTarget({ id: row.id, label: rowLabel(row) })
                  : undefined
              }
            />
          )}
        />

        <ListPagination list={list} label={nounPlural} />
      </Card>

      <ConfirmDeleteModal
        target={deleteTarget}
        path={path}
        noun={noun}
        onClose={() => setDeleteTarget(null)}
        onDeleted={list.refresh}
      />
    </Page>
  );
}

interface ListToolbarProps {
  search: string;
  onSearch: (search: string) => void;
  placeholder: string;
  /** "12 panels"; undefined while loading, when there is no count to show. */
  countLabel: string | undefined;
}

function ListToolbar({ search, onSearch, placeholder, countLabel }: ListToolbarProps) {
  return (
    <div className="c-table-toolbar">
      <div className="c-table-toolbar__search">
        <SearchFilter value={search} onChange={onSearch} placeholder={placeholder} />
      </div>

      {/* The count sits beside the search so the effect of a query is
          visible without looking away to the pagination bar. */}
      {countLabel !== undefined && <span className="c-table-toolbar__count">{countLabel}</span>}

      <DensityControl />
    </div>
  );
}

interface ListEmptyStateProps {
  search: string;
  nounPlural: string;
  emptyBody: string | undefined;
  addAction: ReactNode;
  onClearSearch: () => void;
}

/** Nothing matched the search, or there is nothing yet at all — two different fixes. */
function ListEmptyState({
  search,
  nounPlural,
  emptyBody,
  addAction,
  onClearSearch,
}: ListEmptyStateProps) {
  if (search) {
    return (
      <EmptyState
        icon={<SearchX />}
        title={`No ${nounPlural} match “${search}”`}
        body="Check the spelling, or clear the search to see everything."
        action={<Button onClick={onClearSearch}>Clear search</Button>}
      />
    );
  }

  return (
    <EmptyState
      icon={<Inbox />}
      title={`No ${nounPlural} yet`}
      body={emptyBody}
      action={addAction}
    />
  );
}

interface RowActionsProps {
  /** The row as the buttons' accessible names read it. */
  label: string;
  extra: ReactNode;
  /** Absent when this user may not edit. */
  onEdit?: (() => void) | undefined;
  /** Absent when this user may not delete this row. */
  onDelete?: (() => void) | undefined;
}

function RowActions({ label, extra, onEdit, onDelete }: RowActionsProps) {
  return (
    <>
      {extra}
      {onEdit && (
        <Button size="sm" icon={<Pencil />} aria-label={`Edit ${label}`} onClick={onEdit} />
      )}
      {onDelete && (
        <Button
          size="sm"
          variant="danger"
          icon={<Trash2 />}
          aria-label={`Delete ${label}`}
          onClick={onDelete}
        />
      )}
    </>
  );
}

/** No capability declared means the screen has no separate write gate. */
function useWriteAccess({
  manageCapability: manage,
  createCapability: create = manage,
  editCapability: edit = manage,
  deleteCapability: remove = manage,
}: Pick<
  ResourceListPageProps<never>,
  'manageCapability' | 'createCapability' | 'editCapability' | 'deleteCapability'
>) {
  const allows = useCan();
  const can = (capability: Permission | undefined) =>
    capability === undefined || allows(capability);
  return { canCreate: can(create), canEdit: can(edit), canDelete: can(remove) };
}

function ListPagination({
  list,
  label,
}: {
  list: { page: number; limit: number; count: number; setPage: (page: number) => void };
  label: string;
}) {
  return (
    <Pagination
      page={list.page}
      limit={list.limit}
      count={list.count}
      onPageChange={list.setPage}
      label={label}
    />
  );
}
